import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Empty, Skeleton } from "antd";
import {
  cleanLegendClickStatus,
  setLegendClickStatus,
} from "../../features/global/globalSlice";

const STATUS_META = {
  DeliveredToTerminal: {
    label: "Delivered",
    tone: "success",
  },
  SENT: {
    label: "Queued",
    tone: "info",
  },
  SENT_RETRIED: {
    label: "Retried",
    tone: "info",
  },
  InvalidMsisdn: {
    label: "Invalid number",
    tone: "danger",
  },
  DeliveryImpossible: {
    label: "Delivery impossible",
    tone: "warning",
  },
  AbsentSubscriber: {
    label: "Absent subscriber",
    tone: "warning",
  },
  "Exception sending ": {
    label: "Exception",
    tone: "danger",
  },
};

const TREND_SERIES = [
  {
    key: "deliveredCount",
    label: "Delivered",
    color: "#22c55e",
  },
  {
    key: "sentCount",
    label: "Queued",
    color: "#0ea5e9",
  },
  {
    key: "retriedCount",
    label: "Retried",
    color: "#14b8a6",
  },
  {
    key: "issueCount",
    label: "Issues",
    color: "#f97316",
  },
];

const STATUS_ORDER = [
  "DeliveredToTerminal",
  "SENT",
  "SENT_RETRIED",
  "Exception sending ",
  "InvalidMsisdn",
  "DeliveryImpossible",
  "AbsentSubscriber",
];

const numberFormatter = (value) => Number(value || 0).toLocaleString();

const formatTrendDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
  }).format(date);
};

const buildDefaultRow = (dateKey) => ({
  msgCreatedDate: dateKey,
  deliveredCount: 0,
  sentCount: 0,
  retriedCount: 0,
  issueCount: 0,
  totalCount: 0,
});

const chartTooltipFormatter = (value, name) => [
  numberFormatter(value),
  name,
];

function DashTimeseries({ initialLoad }) {
  const dispatch = useDispatch();
  const { dashData, loading } = useSelector((state) => state.dash);
  const { legendClickStatus } = useSelector((state) => state.global);

  const chartData = useMemo(() => {
    const grouped = {};

    (dashData?.msgTimeSeries ?? []).forEach((item) => {
      const dateKey = item?.msgCreatedDate;
      const status = item?.msgStatus;
      const count = Number(item?.msgCount || 0);

      if (!dateKey) {
        return;
      }

      if (!grouped[dateKey]) {
        grouped[dateKey] = buildDefaultRow(dateKey);
      }

      if (status === "DeliveredToTerminal") {
        grouped[dateKey].deliveredCount += count;
      } else if (status === "SENT") {
        grouped[dateKey].sentCount += count;
      } else if (status === "SENT_RETRIED") {
        grouped[dateKey].retriedCount += count;
      } else {
        grouped[dateKey].issueCount += count;
      }

      grouped[dateKey].totalCount += count;
    });

    return Object.values(grouped).sort(
      (first, second) =>
        new Date(first?.msgCreatedDate).getTime() -
        new Date(second?.msgCreatedDate).getTime()
    );
  }, [dashData]);

  const trendSummary = useMemo(
    () =>
      chartData.reduce(
        (summary, item) => ({
          deliveredCount: summary.deliveredCount + Number(item?.deliveredCount || 0),
          sentCount: summary.sentCount + Number(item?.sentCount || 0),
          retriedCount: summary.retriedCount + Number(item?.retriedCount || 0),
          issueCount: summary.issueCount + Number(item?.issueCount || 0),
        }),
        {
          deliveredCount: 0,
          sentCount: 0,
          retriedCount: 0,
          issueCount: 0,
        }
      ),
    [chartData]
  );

  const availableStatuses = useMemo(
    () =>
      [...new Set((dashData?.ststusSummary ?? []).map((item) => item?.msgStatus).filter(Boolean))]
        .sort((first, second) => {
          const firstIndex = STATUS_ORDER.indexOf(first);
          const secondIndex = STATUS_ORDER.indexOf(second);

          if (firstIndex === -1 && secondIndex === -1) {
            return String(first).localeCompare(String(second));
          }

          if (firstIndex === -1) {
            return 1;
          }

          if (secondIndex === -1) {
            return -1;
          }

          return firstIndex - secondIndex;
        }),
    [dashData]
  );

  return (
    <div className="dashboard-account-chart">
      <div className="dashboard-account-panel-head dashboard-account-chart-head">
        <div>
          <h2>SMS trend</h2>
          <p>Daily message volume across queue, delivery, retry, and issue states.</p>
        </div>
      </div>

      <div className="dashboard-account-chart-summary">
        {TREND_SERIES.map((series) => (
          <div key={series.key} className="dashboard-account-chart-stat">
            <span
              className="dashboard-account-chart-stat-dot"
              style={{ backgroundColor: series.color }}
            />
            <div>
              <small>{series.label}</small>
              <strong>{numberFormatter(trendSummary?.[series.key])}</strong>
            </div>
          </div>
        ))}
      </div>

      {availableStatuses.length > 0 && (
        <div className="dashboard-account-chart-focus">
          <span className="dashboard-account-chart-focus-label">Focus dashboard:</span>

          <button
            type="button"
            onClick={() => dispatch(cleanLegendClickStatus())}
            className={`dashboard-account-chart-pill ${
              !legendClickStatus ? "is-active" : ""
            }`}
          >
            All statuses
          </button>

          {availableStatuses.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => dispatch(setLegendClickStatus(status))}
              className={`dashboard-account-chart-pill tone-${
                STATUS_META[status]?.tone || "neutral"
              } ${legendClickStatus === status ? "is-active" : ""}`}
            >
              {STATUS_META[status]?.label || status}
            </button>
          ))}
        </div>
      )}

      <div className="dashboard-account-chart-frame">
        {initialLoad && loading ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart
              data={chartData}
              margin={{ top: 12, right: 8, left: 0, bottom: 0 }}
              barCategoryGap={18}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#dbe5ef" vertical={false} />
              <XAxis
                dataKey="msgCreatedDate"
                tickFormatter={formatTrendDate}
                tick={{ fill: "#607082", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickMargin={8}
              />
              <YAxis
                allowDecimals={false}
                tickFormatter={numberFormatter}
                tick={{ fill: "#607082", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <Tooltip
                cursor={{ fill: "rgba(55, 138, 221, 0.06)" }}
                labelFormatter={formatTrendDate}
                formatter={chartTooltipFormatter}
                contentStyle={{
                  borderRadius: "16px",
                  border: "1px solid rgba(8, 68, 124, 0.12)",
                  boxShadow: "0 16px 32px rgba(8, 68, 124, 0.12)",
                }}
              />

              {TREND_SERIES.map((series) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={series.label}
                  fill={series.color}
                  radius={[10, 10, 0, 0]}
                  maxBarSize={42}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="dashboard-account-empty-state compact">
            <Empty description="No SMS time series available for this period" />
          </div>
        )}
      </div>
    </div>
  );
}

export default DashTimeseries;
